import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import Clarity from '@microsoft/clarity';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { Component, createRef, RefObject, JSX } from 'react';

import {
  disableUIBlocker,
  enableUIBlocker,
  LoadingSpinner,
  LoadingSpinnerSize,
} from '@/components/common';
import { RedirectStatus } from '@/components/contexts/params';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { triggerErrorToast, triggerInfoToast } from '@/libs/toasts';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import BraintreeGooglePayService from '@/services/braintree/BraintreeGooglePayService';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { getQaTestingProps, QaTestingElements } from '@/utils/qaTesting';

interface IPayWithBraintreeGooglePayBtnProps {
  readonly navigation?: JSX.Element | null;
}

export function PayWithBraintreeGooglePayBtn({
  navigation,
}: IPayWithBraintreeGooglePayBtnProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { userId } = useAuth();
  const paymentModuleT = useUserFriendlyPaymentModuleName();

  return (
    <div className="flex space-x-2">
      {navigation}
      <div className="w-full">
        <GooglePayButton
          locale={language as SupportedLanguage}
          t={t}
          paymentModuleT={paymentModuleT}
          customerNumber={userId}
        />
      </div>
    </div>
  );
}

interface GooglePayButtonProps {
  readonly locale: SupportedLanguage;
  readonly t: (value: string) => string;
  readonly paymentModuleT: (moduleId: PaymentModuleId) => string;
  readonly customerNumber: string | undefined;
}
class GooglePayButton extends Component<GooglePayButtonProps> {
  private ref: RefObject<HTMLDivElement | null>;

  constructor(props: GooglePayButtonProps) {
    super(props);
    this.ref = createRef<HTMLDivElement>();
  }

  componentDidMount() {
    this.injectButton();
  }

  componentDidUpdate(prevProps: GooglePayButtonProps) {
    if (this.props.locale !== prevProps.locale) {
      this.injectButton();
    }
  }

  render() {
    return (
      <div className="h-14 flex flex-col justify-center">
        <div
          className="h-full"
          ref={this.ref}
        >
          <LoadingSpinner size={LoadingSpinnerSize.Small} />
        </div>
      </div>
    );
  }

  private injectButton() {
    if (!this.ref.current) {
      return;
    }

    // Create google pay button
    const btn = BraintreeGooglePayService.createButton(
      this.props.locale,
      {
        onSuccess: this.onSuccess,
        onCancel: this.onCancel,
        onError: this.onError,
        onModalOpen: this.onModalOpen,
      },
      this.props.customerNumber,
    );

    const qaAttrs = getQaTestingProps(QaTestingElements.GooglePayButton);
    Object.entries(qaAttrs).forEach(([name, value]) =>
      btn.setAttribute(name, value),
    );

    // Replace div content with newly created button
    this.ref.current.innerHTML = '';
    this.ref.current.appendChild(btn);
  }

  private onModalOpen = () => {
    enableUIBlocker();
  };

  private onSuccess = () => {
    if (PaymentStore.flow === PaymentIntentFlow.Manage) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('status', RedirectStatus.Success);
      window.location.reload();
    } else {
      NavigationService.navigateTo({ page: InternalPage.StatusSuccess });
      disableUIBlocker();
    }
    Clarity.setTag('pay', [
      RedirectStatus.Success,
      this.props.paymentModuleT(PaymentModuleId.BraintreeGooglePay),
    ]);
  };

  private onCancel = () => {
    disableUIBlocker();
    triggerInfoToast(
      this.props.t('Select a new payment method and proceed to pay.'),
    );
    Clarity.setTag('pay', [
      RedirectStatus.Canceled,
      this.props.paymentModuleT(PaymentModuleId.BraintreeGooglePay),
    ]);
  };

  private onError = (error: Error) => {
    disableUIBlocker();

    TelemetryService.trackException(error, 'PayWithBraintreeGooglePayBtn', {
      pid: PaymentStore.paymentIntentId,
    });
    Logger.error(
      LoggerOrigin.ReactComponent,
      'PayWithBraintreeGooglePayBtn failed to add Google Pay card.',
      error,
    );

    Clarity.setTag('pay', [
      RedirectStatus.Failure,
      this.props.paymentModuleT(PaymentModuleId.BraintreeGooglePay),
    ]);
    triggerErrorToast(
      this.props.t('Something went wrong!'),
      this.props.t(
        'Operation failed. Please try again with a different payment method.',
      ),
    );
  };
}
